/**
 * Payment schedule rules and dynamic installment calculations.
 * Vendors configure rules per package; clients see amounts based on event date.
 */

const MS_PER_DAY = 1000 * 60 * 60 * 24;

export const DUE_TYPES = {
  /** Always collected the day the client places the order */
  NOW: 'now',
  /** Due N days before the event (fixed offset from event date) */
  DAYS_BEFORE_EVENT: 'days_before_event',
  /** Due on a point along the booking→event timeline (0% = booking day, 100% = event day) */
  SPAN_PERCENT: 'span_percent',
};

/** Platform default windows (matches original product rules). */
export const DEFAULT_PAYMENT_SCHEDULE_RULES = {
  use_platform_default: true,
  windows: [
    {
      id: 'full-2-3-weeks',
      label: '2–3 weeks before event',
      min_days: 14,
      max_days: 21,
      description:
        '100% payment is due when booking if the event is 2–3 weeks away.',
      installments: [
        { label: 'Full Payment', percentage: 100, due: { type: DUE_TYPES.NOW } },
      ],
    },
    {
      id: 'under-1-month',
      label: '3–4 weeks before event',
      min_days: 22,
      max_days: 29,
      description:
        '50% deposit when you order; balance due later in the booking period before the event.',
      installments: [
        { label: 'Booking deposit', percentage: 50, due: { type: DUE_TYPES.NOW } },
        {
          label: 'Balance',
          percentage: 50,
          due: { type: DUE_TYPES.SPAN_PERCENT, percent: 85 },
        },
      ],
    },
    {
      id: 'deposit-1-5-months',
      label: '1–5 months before event',
      min_days: 30,
      max_days: 150,
      description:
        '30% deposit when you order; other payments spread between booking and the event.',
      installments: [
        { label: 'Booking deposit', percentage: 30, due: { type: DUE_TYPES.NOW } },
        {
          label: 'Mid payment',
          percentage: 35,
          due: { type: DUE_TYPES.SPAN_PERCENT, percent: 50 },
        },
        {
          label: 'Final payment',
          percentage: 35,
          due: { type: DUE_TYPES.SPAN_PERCENT, percent: 90 },
        },
      ],
    },
    {
      id: 'deposit-6-9-months',
      label: '6–9 months before event',
      min_days: 151,
      max_days: 270,
      description:
        '50% deposit to book; remaining 50% at least 2 weeks before the event.',
      installments: [
        { label: 'Deposit', percentage: 50, due: { type: DUE_TYPES.NOW } },
        {
          label: 'Final Payment',
          percentage: 50,
          due: { type: DUE_TYPES.DAYS_BEFORE_EVENT, days: 14 },
        },
      ],
    },
    {
      id: 'long-10-24-months',
      label: '10–24 months before event',
      min_days: 271,
      max_days: 730,
      description:
        '30% deposit now; 40% by 2 months before event (70% total paid); final 30% at least 2 weeks before the event.',
      installments: [
        { label: 'Initial Deposit', percentage: 30, due: { type: DUE_TYPES.NOW } },
        {
          label: 'Second Payment',
          percentage: 40,
          due: { type: DUE_TYPES.DAYS_BEFORE_EVENT, days: 60 },
        },
        {
          label: 'Final Payment',
          percentage: 30,
          due: { type: DUE_TYPES.DAYS_BEFORE_EVENT, days: 14 },
        },
      ],
    },
    {
      id: 'long-24-plus',
      label: 'More than 24 months before event',
      min_days: 731,
      max_days: null,
      description:
        '30% deposit now; 40% by 2 months before event; final 30% at least 2 weeks before the event.',
      installments: [
        { label: 'Initial Deposit', percentage: 30, due: { type: DUE_TYPES.NOW } },
        {
          label: 'Second Payment',
          percentage: 40,
          due: { type: DUE_TYPES.DAYS_BEFORE_EVENT, days: 60 },
        },
        {
          label: 'Final Payment',
          percentage: 30,
          due: { type: DUE_TYPES.DAYS_BEFORE_EVENT, days: 14 },
        },
      ],
    },
    {
      id: 'rush-under-2-weeks',
      label: 'Less than 2 weeks before event',
      min_days: 0,
      max_days: 13,
      description: '100% payment is due immediately when booking.',
      installments: [
        { label: 'Full Payment', percentage: 100, due: { type: DUE_TYPES.NOW } },
      ],
    },
  ],
};

const startOfDay = (value) => {
  const d = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
};

export const getDaysUntilEvent = (eventDate, bookingDate = new Date()) => {
  const event = startOfDay(eventDate);
  const booking = startOfDay(bookingDate);
  if (!event || !booking) return null;
  return Math.ceil((event - booking) / MS_PER_DAY);
};

const formatDueDateLabel = (date) =>
  date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const normalizeDue = (due) => {
  if (!due || typeof due !== 'object') return { type: DUE_TYPES.NOW };

  if (due.type === DUE_TYPES.DAYS_BEFORE_EVENT) {
    return {
      type: DUE_TYPES.DAYS_BEFORE_EVENT,
      days: Math.max(0, parseInt(due.days, 10) || 0),
    };
  }

  if (due.type === DUE_TYPES.SPAN_PERCENT) {
    const percent = Math.max(0, Math.min(100, parseFloat(due.percent) ?? 0));
    return { type: DUE_TYPES.SPAN_PERCENT, percent };
  }

  return { type: DUE_TYPES.NOW };
};

const normalizeInstallment = (raw, index) => ({
  label: String(raw?.label || `Payment ${index + 1}`).trim(),
  percentage: Math.max(0, Math.min(100, parseFloat(raw?.percentage) || 0)),
  due: normalizeDue(raw?.due),
});

const normalizeWindow = (raw, index) => {
  const minDays = Math.max(0, parseInt(raw?.min_days, 10) || 0);
  const maxRaw = raw?.max_days;
  const maxDays =
    maxRaw === null || maxRaw === undefined || maxRaw === ''
      ? null
      : Math.max(minDays, parseInt(maxRaw, 10) || minDays);

  const installments = (Array.isArray(raw?.installments) ? raw.installments : []).map(
    normalizeInstallment
  );

  return {
    id: raw?.id || `window-${index}`,
    label: String(raw?.label || `Window ${index + 1}`).trim(),
    min_days: minDays,
    max_days: maxDays,
    description: String(raw?.description || '').trim(),
    installments,
  };
};

/** True when package should use platform default schedule (not vendor custom). */
export const isPlatformDefaultPaymentRules = (rules) =>
  !rules || rules.use_platform_default === true;

/** Rules used for client payment math (always includes full window list). */
export const rulesForPaymentCalculation = (rules) => {
  if (isPlatformDefaultPaymentRules(rules)) {
    return JSON.parse(JSON.stringify(DEFAULT_PAYMENT_SCHEDULE_RULES));
  }
  return normalizeCustomPaymentScheduleRules(rules);
};

/**
 * Validate and normalize vendor custom rules (editor / API custom mode only).
 */
export const normalizeCustomPaymentScheduleRules = (rules) => {
  const windows = (Array.isArray(rules?.windows) ? rules.windows : [])
    .map(normalizeWindow)
    .filter((w) => w.installments.length > 0);

  windows.forEach((window) => {
    const total = window.installments.reduce((s, i) => s + i.percentage, 0);
    if (Math.abs(total - 100) > 0.01) {
      window._percentageWarning = `Installments total ${total}% (should be 100%)`;
    }
  });

  return {
    use_platform_default: false,
    windows,
  };
};

/** @deprecated Use rulesForPaymentCalculation for checkout; normalizeCustomPaymentScheduleRules for editor. */
export const normalizePaymentScheduleRules = (rules) => rulesForPaymentCalculation(rules);

export const validatePaymentScheduleRules = (rules) => {
  if (isPlatformDefaultPaymentRules(rules)) {
    return {
      valid: true,
      errors: [],
      normalized: { use_platform_default: true },
    };
  }

  const normalized = normalizeCustomPaymentScheduleRules(rules);
  const errors = [];

  if (!normalized.windows.length) {
    errors.push('Add at least one booking window with installments.');
  }

  normalized.windows.forEach((window, wi) => {
    const total = window.installments.reduce((s, i) => s + i.percentage, 0);
    if (Math.abs(total - 100) > 0.01) {
      errors.push(`Window "${window.label}": installments must total 100% (currently ${total}%).`);
    }
    if (window.max_days != null && window.max_days < window.min_days) {
      errors.push(`Window "${window.label}": max days must be ≥ min days.`);
    }
    window.installments.forEach((inst, ii) => {
      if (inst.percentage <= 0) {
        errors.push(`Window "${window.label}", payment ${ii + 1}: percentage must be > 0.`);
      }
    });

    const hasBookingDeposit = window.installments.some(
      (inst) => inst.due.type === DUE_TYPES.NOW
    );
    if (!hasBookingDeposit) {
      errors.push(
        `Window "${window.label}": add at least one installment due at booking (deposit on order day).`
      );
    }
  });

  return { valid: errors.length === 0, errors, normalized };
};

const windowMatchesDays = (window, daysUntilEvent) => {
  if (daysUntilEvent == null || daysUntilEvent < 0) return false;
  if (daysUntilEvent < window.min_days) return false;
  if (window.max_days != null && daysUntilEvent > window.max_days) return false;
  return true;
};

/**
 * Pick the best matching window; falls back to nearest applicable window so long-range events still work.
 */
export const matchPaymentWindow = (daysUntilEvent, rulesConfig) => {
  const rules = rulesForPaymentCalculation(rulesConfig);
  if (daysUntilEvent == null || daysUntilEvent < 0 || !rules.windows?.length) return null;

  const sorted = [...rules.windows].sort((a, b) => b.min_days - a.min_days);
  const exact = sorted.find((w) => windowMatchesDays(w, daysUntilEvent));
  if (exact) return exact;

  const withinMin = sorted.filter((w) => daysUntilEvent >= w.min_days);
  if (withinMin.length) {
    const openEnded = withinMin.find((w) => w.max_days == null);
    if (openEnded) return openEnded;
    return withinMin[0];
  }

  const byNearestMin = [...sorted].sort(
    (a, b) =>
      Math.abs(daysUntilEvent - a.min_days) - Math.abs(daysUntilEvent - b.min_days)
  )[0];
  return byNearestMin || sorted[0];
};

const capDueBeforeEvent = (dueBy, event) => {
  if (!dueBy || !event) return dueBy;
  if (dueBy >= event) {
    const capped = new Date(event);
    capped.setDate(capped.getDate() - 1);
    return capped;
  }
  return dueBy;
};

const resolveDueDate = (due, eventDate, bookingDate) => {
  const event = startOfDay(eventDate);
  const booking = startOfDay(bookingDate);
  if (!event || !booking) {
    return {
      dueDate: 'Due now to book date',
      dueBy: null,
      isDueNow: true,
      isBookingDeposit: due.type === DUE_TYPES.NOW,
    };
  }

  if (due.type === DUE_TYPES.NOW) {
    return {
      dueDate: 'Due now to book date (day you order)',
      dueBy: booking.toISOString(),
      isDueNow: true,
      isBookingDeposit: true,
    };
  }

  if (due.type === DUE_TYPES.SPAN_PERCENT) {
    const spanDays = getDaysUntilEvent(eventDate, bookingDate) ?? 0;
    const pct = Math.max(0, Math.min(100, parseFloat(due.percent) ?? 0));
    const offsetDays =
      spanDays <= 0 ? 0 : Math.max(0, Math.min(spanDays - 1, Math.round((spanDays * pct) / 100)));
    let dueBy = new Date(booking);
    dueBy.setDate(dueBy.getDate() + offsetDays);
    dueBy = capDueBeforeEvent(dueBy, event);

    const isDueNow = dueBy <= booking;
    const label = isDueNow
      ? 'Due now to book date (timeline point already reached)'
      : `Due by ${formatDueDateLabel(dueBy)} (${pct}% through booking period)`;

    return {
      dueDate: label,
      dueBy: dueBy.toISOString(),
      isDueNow,
      isBookingDeposit: false,
    };
  }

  if (due.type === DUE_TYPES.DAYS_BEFORE_EVENT) {
    let dueBy = new Date(event);
    dueBy.setDate(dueBy.getDate() - (due.days || 0));
    dueBy = capDueBeforeEvent(dueBy, event);
    const isDueNow = dueBy <= booking;
    const label =
      due.days === 14
        ? `Due by ${formatDueDateLabel(dueBy)} (2 weeks before event)`
        : due.days === 60
          ? `Due by ${formatDueDateLabel(dueBy)} (2 months before event)`
          : `Due by ${formatDueDateLabel(dueBy)} (${due.days} days before event)`;
    return {
      dueDate: isDueNow ? 'Due now to book date (deadline already passed)' : label,
      dueBy: dueBy.toISOString(),
      isDueNow,
      isBookingDeposit: false,
    };
  }

  return {
    dueDate: 'Due now to book date',
    dueBy: booking.toISOString(),
    isDueNow: true,
    isBookingDeposit: true,
  };
};

/**
 * Build full payment terms for an event date and package rules.
 */
export const resolvePaymentTerms = (
  eventDate,
  subtotal = 0,
  rulesConfig = null,
  options = {}
) => {
  const { bookingDate = new Date(), platformFee = 0, tax = 0 } = options;
  const taxAmount = Number(tax) || 0;

  if (!eventDate) return null;

  const daysUntilEvent = getDaysUntilEvent(eventDate, bookingDate);
  if (daysUntilEvent == null || daysUntilEvent < 0) return null;

  const window = matchPaymentWindow(daysUntilEvent, rulesConfig);
  if (!window) return null;

  const schedule = window.installments.map((inst, index) => {
    const dueMeta = resolveDueDate(inst.due, eventDate, bookingDate);
    const subtotalPortion = (subtotal * inst.percentage) / 100;
    const feePortion = (platformFee * inst.percentage) / 100;
    const taxPortion = (taxAmount * inst.percentage) / 100;
    const totalAmount = Math.round((subtotalPortion + feePortion + taxPortion) * 100) / 100;

    return {
      label: inst.label,
      percentage: inst.percentage,
      dueType: inst.due.type,
      dueDate: dueMeta.dueDate,
      dueBy: dueMeta.dueBy,
      isDueNow: dueMeta.isDueNow,
      isBookingDeposit: dueMeta.isBookingDeposit,
      spanPercent: inst.due.type === DUE_TYPES.SPAN_PERCENT ? inst.due.percent : null,
      subtotalAmount: Math.round(subtotalPortion * 100) / 100,
      platformFeePortion: Math.round(feePortion * 100) / 100,
      taxPortion: Math.round(taxPortion * 100) / 100,
      totalAmount,
      index,
    };
  });

  const bookingDepositPercentage = schedule
    .filter((p) => p.isBookingDeposit)
    .reduce((s, p) => s + p.percentage, 0);

  const checkoutNowPercentage = schedule
    .filter((p) => p.isDueNow)
    .reduce((s, p) => s + p.percentage, 0);

  const bookingDepositAmount = schedule
    .filter((p) => p.isBookingDeposit)
    .reduce((s, p) => s + p.totalAmount, 0);

  const amountDueNow = schedule
    .filter((p) => p.isDueNow)
    .reduce((s, p) => s + p.totalAmount, 0);

  return {
    daysUntilEvent,
    bookingSpanDays: daysUntilEvent,
    matchedWindowId: window.id,
    matchedWindowLabel: window.label,
    description: window.description,
    depositPercentage: bookingDepositPercentage,
    depositLabel: `${bookingDepositPercentage}%`,
    bookingDepositAmount: Math.round(bookingDepositAmount * 100) / 100,
    checkoutNowPercentage,
    schedule,
    amountDueNow: Math.round(amountDueNow * 100) / 100,
    totalDue: Math.round((subtotal + platformFee + taxAmount) * 100) / 100,
    taxAmount: Math.round(taxAmount * 100) / 100,
    useCustomRules: rulesConfig && rulesConfig.use_platform_default === false,
  };
};

/**
 * @deprecated Use resolvePaymentTerms — kept for backward compatibility.
 */
export const calculatePaymentTerms = (eventDate, subtotal = 0, rulesConfig = null) => {
  return resolvePaymentTerms(eventDate, subtotal, rulesConfig);
};

export const calculatePaymentAmount = (subtotal, platformFee, percentage, tax = 0) => {
  const subtotalAmount = subtotal * (percentage / 100);
  const proportionalFee = platformFee * (percentage / 100);
  const proportionalTax = (Number(tax) || 0) * (percentage / 100);
  return Math.round((subtotalAmount + proportionalFee + proportionalTax) * 100) / 100;
};

export const getAmountDueNow = (paymentTerms, subtotal, platformFee) => {
  const terms =
    paymentTerms?.amountDueNow != null
      ? paymentTerms
      : resolvePaymentTerms(
          paymentTerms?.eventDate,
          subtotal,
          null,
          { platformFee }
        );
  if (!terms) {
    return calculatePaymentAmount(subtotal, platformFee, 50);
  }
  if (terms.amountDueNow != null) return terms.amountDueNow;
  return calculatePaymentAmount(subtotal, platformFee, terms.depositPercentage || 50);
};

export const calculateRemainingPayments = (
  paymentTerms,
  completedPayments,
  subtotal,
  platformFee
) => {
  if (!paymentTerms?.schedule) return [];

  const totalPaid = completedPayments.reduce(
    (sum, payment) => sum + parseFloat(payment.amount || 0),
    0
  );
  const totalDue = subtotal + platformFee;
  const completedPercentages = totalDue > 0 ? (totalPaid / totalDue) * 100 : 0;

  return paymentTerms.schedule
    .map((payment, index) => {
      let cumulativePercentage = 0;
      for (let i = 0; i <= index; i++) {
        cumulativePercentage += paymentTerms.schedule[i].percentage;
      }

      let previousCumulativePercentage = 0;
      for (let i = 0; i < index; i++) {
        previousCumulativePercentage += paymentTerms.schedule[i].percentage;
      }

      const isPaid = cumulativePercentage <= completedPercentages + 0.01;
      const isPartiallyPaid =
        completedPercentages > previousCumulativePercentage &&
        completedPercentages < cumulativePercentage;

      const paymentAmount =
        payment.totalAmount ??
        calculatePaymentAmount(subtotal, platformFee, payment.percentage);

      let remainingAmount = paymentAmount;
      if (isPartiallyPaid) {
        const remainingPercentage = cumulativePercentage - completedPercentages;
        remainingAmount = calculatePaymentAmount(subtotal, platformFee, remainingPercentage);
      }

      return {
        ...payment,
        index,
        cumulativePercentage,
        previousCumulativePercentage,
        isPaid,
        isPartiallyPaid,
        amount: paymentAmount,
        remainingAmount,
      };
    })
    .filter((payment) => !payment.isPaid);
};

export const getNextPaymentOption = (
  paymentTerms,
  completedPayments,
  subtotal,
  platformFee
) => {
  const remainingPayments = calculateRemainingPayments(
    paymentTerms,
    completedPayments,
    subtotal,
    platformFee
  );
  return remainingPayments.length > 0 ? remainingPayments[0] : null;
};

/** Preview terms for vendor UI (package price only — no platform commission). */
export const previewPaymentTermsForRules = (
  rulesConfig,
  subtotal = 370,
  platformFee = 0
) => {
  const samples = [
    { label: 'Event in 3 weeks', date: addDaysFromToday(21) },
    { label: 'Event in 3 months', date: addDaysFromToday(90) },
    { label: 'Event in 8 months', date: addDaysFromToday(240) },
  ];

  const calcRules = rulesForPaymentCalculation(rulesConfig);

  return samples.map((sample) => ({
    ...sample,
    terms: resolvePaymentTerms(sample.date, subtotal, calcRules, { platformFee }),
  }));
};

/** Shape stored in form state when loading from API. */
export const parsePaymentScheduleRulesFromPackage = (stored) => {
  if (!stored || stored.use_platform_default !== false) {
    return { use_platform_default: true };
  }
  return normalizeCustomPaymentScheduleRules(stored);
};

function addDaysFromToday(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export const createEmptyCustomRules = () =>
  JSON.parse(
    JSON.stringify({
      use_platform_default: false,
      windows: [
        {
          id: `window-rush-${Date.now()}`,
          label: 'Less than 1 month away',
          min_days: 0,
          max_days: 29,
          description:
            'Deposit due when you order; remaining balance is spread before the event based on your timeline.',
          installments: [
            { label: 'Booking deposit', percentage: 50, due: { type: DUE_TYPES.NOW } },
            {
              label: 'Balance',
              percentage: 50,
              due: { type: DUE_TYPES.SPAN_PERCENT, percent: 85 },
            },
          ],
        },
        {
          id: `window-mid-${Date.now()}`,
          label: '1–6 months away',
          min_days: 30,
          max_days: 180,
          description:
            'Deposit on order day; other payments land on milestones between booking and event.',
          installments: [
            { label: 'Booking deposit', percentage: 30, due: { type: DUE_TYPES.NOW } },
            {
              label: 'Mid payment',
              percentage: 35,
              due: { type: DUE_TYPES.SPAN_PERCENT, percent: 50 },
            },
            {
              label: 'Final payment',
              percentage: 35,
              due: { type: DUE_TYPES.SPAN_PERCENT, percent: 90 },
            },
          ],
        },
        {
          id: `window-long-${Date.now()}`,
          label: 'More than 6 months away',
          min_days: 181,
          max_days: null,
          description:
            'Deposit when booking; follow-up payments spread across the months until your event.',
          installments: [
            { label: 'Booking deposit', percentage: 25, due: { type: DUE_TYPES.NOW } },
            {
              label: 'Second payment',
              percentage: 35,
              due: { type: DUE_TYPES.SPAN_PERCENT, percent: 40 },
            },
            {
              label: 'Third payment',
              percentage: 20,
              due: { type: DUE_TYPES.SPAN_PERCENT, percent: 70 },
            },
            {
              label: 'Final payment',
              percentage: 20,
              due: { type: DUE_TYPES.SPAN_PERCENT, percent: 92 },
            },
          ],
        },
      ],
    })
  );
